import React, { type FC } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { LinkMenuItem } from '@atlaskit/navigation-system/side-nav-items/link-menu-item';

import { HOME } from '@confluence/named-routes/entry-points/routes.ts';
import { useRouteDataRef, useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';

import { TaskIconComponent } from '../GlobalNavigationIcons.tsx';
import { i18n } from '../globalNavigationTranslations.ts';

import { useGlobalItemVisibility } from './useGlobalItemVisibility.ts';
import type { GlobalItemProps } from './globalItemProps.ts';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ '../analyticsCallbacks.ts'))
			.fireTasksClickedAnalytics,
);

const onTasksSelector = {
	selector: (routeUrl: string | undefined) => {
		if (!routeUrl) {
			return false;
		}
		return routeUrl === HOME.toUrl({ view: 'tasks' });
	},
};

export const TasksItem: FC<GlobalItemProps> = ({ isHidden, peekingId, setPeekingId }) => {
	const routeDataRef = useRouteDataRef();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);

	const isSelected = useRouteUrl(onTasksSelector);
	const shouldHide = useGlobalItemVisibility(
		'tasks',
		isSelected,
		isHidden,
		peekingId,
		setPeekingId,
	);

	if (shouldHide) {
		return null;
	}

	return (
		<LinkMenuItem
			href={HOME.toUrl({ view: 'tasks' })}
			elemBefore={TaskIconComponent}
			isSelected={isSelected}
			onClick={fireClickAnalytics}
		>
			<FormattedMessage {...i18n.tasks} />
		</LinkMenuItem>
	);
};
